import { Component, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, startWith, map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { A11yModule } from '@angular/cdk/a11y';
import { ThemeService } from '../../../@fury/services/theme.service';
import { SearchService } from 'src/app/main/shared/services/search.service';
import { navigateToSearchResult } from './search-navigation';
import { ToolbarFullscreenToggleComponent } from './toolbar-fullscreen-toggle/toolbar-fullscreen-toggle.component';
import { ToolbarNotificationsComponent } from './toolbar-notifications/toolbar-notifications.component';
import { ToolbarQuickpanelToggleComponent } from './toolbar-quickpanel-toggle/toolbar-quickpanel-toggle.component';
import { ToolbarSearchBarComponent } from './toolbar-search-bar/toolbar-search-bar.component';
import { ToolbarSearchComponent } from './toolbar-search/toolbar-search.component';
import { ToolbarSidenavMobileToggleComponent } from './toolbar-sidenav-mobile-toggle/toolbar-sidenav-mobile-toggle.component';
import { ToolbarUserComponent } from './toolbar-user/toolbar-user.component';

@Component({
  selector: 'fury-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatAutocompleteModule,
    A11yModule,
    ToolbarSidenavMobileToggleComponent,
    ToolbarQuickpanelToggleComponent,
    ToolbarUserComponent,
  ],
})
export class ToolbarComponent implements OnInit {
  @Input()
  @HostBinding('class.no-box-shadow')
  hasNavigation: boolean;

  @Output() openSidenav = new EventEmitter();
  @Output() openQuickPanel = new EventEmitter();
  
  @ViewChild('searchBox') searchInput!: ElementRef;
  
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Alt+S to open search
    if (event.altKey && event.key === 's') {
      event.preventDefault();
      this.showSearch = true;
      // Focus the search input after a brief delay to ensure it's rendered
      setTimeout(() => {
        if (this.searchInput && this.searchInput.nativeElement) {
          this.searchInput.nativeElement.focus();
        }
      }, 100);
    }
    // Escape to close search
    if (event.key === 'Escape' && this.showSearch) {
      event.preventDefault();
      this.showSearch = false;
    }
  }

  topNavigation$ = this.themeService.config$.pipe(map((config) => config.navigation === 'top'));

  showSearch = false;
  showLoader = false; // Replace with actual loader logic if needed
  searchControl = new FormControl('');
  filteredItems!: Observable<any[]>;
  hasSearchTerm = false;
  isSearching = false;
  hasSearched = false;
  title = '';

  constructor(
    private themeService: ThemeService,
    private searchService: SearchService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Track whether we have a search term
    this.searchControl.valueChanges.pipe(
      startWith('')
    ).subscribe((value: any) => {
      const searchTerm = typeof value === 'string' ? value : value?.title || '';
      this.hasSearchTerm = searchTerm && searchTerm.trim() !== '';
    });

    this.filteredItems = this.searchControl.valueChanges.pipe(
      startWith(''),
      tap((value: any) => {
        const searchTerm = typeof value === 'string' ? value : value?.title || '';
        // Mark as searching when we have a search term
        if (searchTerm && searchTerm.trim() !== '') {
          this.isSearching = true;
        }
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: any) => {
        // Handle both string values and object values
        const searchTerm = typeof value === 'string' ? value : value?.title || '';
        
        // Don't search if the search term is empty
        if (!searchTerm || searchTerm.trim() === '') {
          this.isSearching = false;
          this.hasSearched = false;
          return of([]);
        }
        
        return this.searchService.search(searchTerm).pipe(
          tap(() => {
            this.isSearching = false;
            this.hasSearched = true;
          })
        );
      })
    );
  }

  displayWith(item: any): string {
    return item && item.title ? item.title : '';
  }

  toggleSearch(): void {
    this.showSearch = !this.showSearch;
    if (this.showSearch) {
      // Focus the search input after a brief delay to ensure it's rendered
      setTimeout(() => {
        if (this.searchInput && this.searchInput.nativeElement) {
          this.searchInput.nativeElement.focus();
        }
      }, 100);
    }
  }

  selectSearchItem(item: any): void {
    // Clear the search control
    this.searchControl.setValue('', { emitEvent: false });
    navigateToSearchResult(this.router, item);
    this.showSearch = false;
  }
}
